import { createContext, useContext, useMemo, useRef, useState, type ReactNode } from "react";

interface TimerContextValue {
  /** Elapsed seconds for the main question timer (updated via ref — does not trigger re-renders). */
  elapsedSecondsRef: React.MutableRefObject<number>;
  /** Remaining seconds for the 20s second-chance countdown (updated via ref). */
  secondChanceSecondsRef: React.MutableRefObject<number>;
  /** Whether the 20s second-chance timer is currently active. */
  isSecondChanceActive: boolean;
  setIsSecondChanceActive: (active: boolean) => void;
}

const TimerContext = createContext<TimerContextValue | undefined>(undefined);

export const TimerProvider = ({ children }: { children: ReactNode }) => {
  const elapsedSecondsRef = useRef(0);
  const secondChanceSecondsRef = useRef(0);
  const [isSecondChanceActive, setIsSecondChanceActive] = useState(false);

  const value = useMemo(
    () => ({
      elapsedSecondsRef,
      secondChanceSecondsRef,
      isSecondChanceActive,
      setIsSecondChanceActive,
    }),
    [isSecondChanceActive],
  );

  return <TimerContext.Provider value={value}>{children}</TimerContext.Provider>;
};

export const useTimer = (): TimerContextValue => {
  const ctx = useContext(TimerContext);
  if (!ctx) {
    throw new Error("useTimer must be used within a TimerProvider");
  }
  return ctx;
};
